import { useFeatureFlagEnabled } from "@posthog/react";
import { posthogEnabled } from "@/services/analytics/enabled";

/**
 * Feature flags served by PostHog. Use the PostHog UI to configure them and turn them on and off.
 *
 * The static config in feature-flags.ts still owns the older flags; this is the target pattern they migrate onto.
 *
 */
export const POSTHOG_FEATURE_FLAG = {
    ON_DEMAND_IRRIGATION: "on-demand-irrigation-launch",
    // Business requirement: Grandfathered orgs we owe on-demand irrigation regardless of how slowly their zones actuate
    ON_DEMAND_IRRIGATION_RESPONSE_TIME_BYPASS: "on-demand-irrigation-response-time-bypass",
    // Pre-release; target internal users only
    DATA_VIEWER: "data-viewer",
    // The one carve-out: orgs that get the data viewer on ePlant device cards before release
    EPLANT_DEVICE_CARD_DATA_VIEWER: "data-viewer-eplant-device-card",
    // Hides the station's marker, which is the only way to its card
    WEATHER_STATION: "weather-station",
    // A station's threshold ranges have nowhere to save to yet
    WEATHER_STATION_THRESHOLD_ALERTS: "weather-station-threshold-alerts",
} as const;

export type PostHogFeatureFlag = (typeof POSTHOG_FEATURE_FLAG)[keyof typeof POSTHOG_FEATURE_FLAG];

/**
 * Whether a PostHog flag is on for the current organization. Flags load asynchronously, so this may initially be false for the first render or two
 *
 * Anything short of a definite yes reads as "off", so an unreachable PostHog or a flag that does not exist yet hides the feature.
 *
 * Local Dev exception: the posthog SDK is never booted there, so every flag is treated as ON rather than
 * hiding an in-development feature. Safe to call the hook regardless since it falls back to false if PostHog is not initialized.
 */
export function usePostHogFeatureFlag(flag: PostHogFeatureFlag): boolean {
    // false is the default value if the flag is not set or posthog unreachable
    const isEnabledInPostHog = useFeatureFlagEnabled(flag, false);

    return !posthogEnabled || isEnabledInPostHog;
}
